/* eslint-disable sonarjs/cognitive-complexity */
import { FC, useEffect, useState, useCallback, useRef } from "react";
import React from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import Link from "next/link";
import Breadcrumb from "@Components/breadcrumb/Breadcrumb";
import {
  getItemQty,
  getWeightValue,
  getAllBadges,
  formatNetWeight,
  getUomValue,
  getPriceOrDiscounted,
  formatUnitPrice,
  formatUnitPriceCA,
  getQtyPerPackage,
  getCTUnitPrice,
  filterItemDetailsForDataLayer,
  FilterItemType,
  getMinAndMaxWeight,
} from "@Components/cart/helpers/helpers";
import ButtonCartAddOrUpdate from "@Components/ui/buttons/button-cart-add-or-update/ButtonCartAddOrUpdate";
import ReadMore from "@Components/ui/readMore/ReadMore";
import { StarRating } from "@Components/ui/star-rating/StarRating";
import { nextAppEnv } from "@Utils/environment";

import { currency } from "@Utils/index";
import { isCanadianLocale } from "@Utils/locale";
import Icon from "@wff/components/ui/icon";
import { useBreadcrumb } from "@wff/hooks/useBreadcrumb";
import { useCustomer } from "@wff/hooks/useCustomer";
import { useFavorites } from "@wff/hooks/useFavorites";
import { useTrackViewedProduct } from "@wff/hooks/useKlaviyo";
import { ICartLineItem } from "@wff/interfaces";
import { useAppSelector, useAppDispatch } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import Badge from "./badge/Badge";
import {
  getPrepTips,
  getCountryOfOrigin,
  getSetItemProducts,
} from "./helpers/ProductDetailHelpers";
import { ProductAccordion } from "./product-accordion";
import ProductGallery from "./product-gallery/ProductGallery";
import classes from "./ProductDetails.module.scss";

export interface IUOMPriceDisplay {
  qtyPerPackage: string;
  weight: string;
  UOM: string;
  unitPrice: string;
  currentLocale: string;
  netWeight: string;
  unitPriceCA?: string;
}

export interface ProductDetailsProps {
  productData: any; // TODO: make a legitimate type for this
  reviewsRef: any;
}
const GTMViewContent = (data: any, actualCurrency: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "ViewContent",
    ViewContent: {
      content_type: "Product",
      contents: data,
      currency: actualCurrency,
      value: getPriceOrDiscounted(data.masterVariant.prices),
    },
  });

  // Updated dataLayer as per CE-1452
  window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  const filteredItem = filterItemDetailsForDataLayer(
    data,
    FilterItemType.ViewItem
  );
  window.dataLayer.push({
    event: "view_item",
    ecommerce: {
      currency: actualCurrency,
      value: getPriceOrDiscounted(data.masterVariant.prices),
      items: [filteredItem],
    },
  });
};

export const ProductDetails: FC<ProductDetailsProps> = ({
  productData,
  reviewsRef,
}) => {
  const { width } = useWindowDimensions();
  const dispatch = useAppDispatch();
  const { lineItems } = useAppSelector((state) => state.cart);
  const SKU = productData?.ctData?.masterData?.current?.masterVariant?.sku;
  const itemIndex = lineItems?.findIndex(
    (item: ICartLineItem) => item.sku === SKU
  );
  const [itemQuantity, setItemQuantity] = useState(
    getItemQty(lineItems, itemIndex)
  );
  const [qtyInputValue, setQtyInputValue] = useState(
    getItemQty(lineItems, itemIndex)
  );
  const currentLocale = useAppSelector(localeSelector)?.languageCode;

  const currentProduct = productData?.ctData?.masterData?.current;
  const [favIcon, setFavIcon] = useState("heart-outlined");

  const isFavoritesEnableForCA =
    nextAppEnv.feature_toggle.CA.favorites === "true" ? true : false;

  //Trigger 'View Product' Klaviyo Event
  useTrackViewedProduct(currentProduct);

  const pageContent = productData?.pageContent;
  const isFixedPrice =
    productData?.ctData?.productType?.obj?.key === "fixedPriceItem"
      ? true
      : false;
  const lang = currentLocale;
  const attribs = currentProduct?.masterVariant?.attributes;
  const title = currentProduct?.name ? currentProduct?.name[lang] : "";
  const description = currentProduct?.description
    ? currentProduct?.description[lang]
    : "";
  const badges = getAllBadges(attribs, lang);
  const actualCurrency = currentLocale === "en-CA" ? "CAD" : "USD";
  const priceData = currentProduct?.masterVariant?.prices;
  const channels = currentProduct?.masterVariant?.availability?.channels;
  const weight = getWeightValue(attribs);
  const UOM = getUomValue(attribs);
  const ctUnitPrice = getCTUnitPrice(attribs);

  const availability =
    currentProduct?.masterVariant?.availability?.availableQuantity;
  const checkProductIsOnStock = () => {
    const isProductAvailable =
      channels &&
      Object.keys(channels).find((key) => {
        return channels[key]?.isOnStock === true;
      });
    return isProductAvailable
      ? true
      : currentProduct?.masterVariant?.availability?.isOnStock;
  };
  const isOnStock = checkProductIsOnStock();
  const qtyPerPackage = getQtyPerPackage(attribs);
  const netWeight = formatNetWeight(weight, UOM, currentLocale, isFixedPrice);
  const unitPrice = formatUnitPrice(ctUnitPrice, UOM);
  const variableMinAndMaxWeight = getMinAndMaxWeight(attribs, UOM);
  const unitPriceCA = formatUnitPriceCA(priceData, weight, UOM, currentLocale);
  const totalPrice = currency.format(getPriceOrDiscounted(priceData));
  const productPrice = getPriceOrDiscounted(priceData);
  const countryOfOriginLabel = pageContent?.countryOfOriginLabel;
  const countryOfOrigin = getCountryOfOrigin(attribs);
  const finalPriceMessage = pageContent?.finalPriceMessage;
  const imageSet = pageContent?.productData?.imageSet;
  const prepTips = getPrepTips(attribs, currentLocale);
  const recipes = pageContent?.productData?.recipes;
  const accordionData = {
    expandable1Content: pageContent?.expandable1Content,
    expandable1Label: pageContent?.expandable1Label,
    expandable2Label: pageContent?.expandable2Label,
    expandable3Label: pageContent?.expandable3Label,
    prepTips: prepTips,
    nutritionImage: imageSet?.nutritionFactsImage,
    isFixedPrice: isFixedPrice,
    recipes: recipes,
  };

  const productSetItems = getSetItemProducts(
    currentProduct?.masterVariant?.attributes
  );

  const isMobile = useMediaQuery("(max-width:960px)");
  const isMobileAndNotTablet = useMediaQuery("(max-width:479px)");

  useEffect(() => {
    GTMViewContent(currentProduct, actualCurrency);
  }, [currentProduct, actualCurrency]);

  const incrementalPayload = {
    itemIndex: itemIndex,
    itemQuantity: itemQuantity,
    availability: availability,
    sku: SKU,
    callback: setItemQuantity,
    dispatch: dispatch,
  };

  const decrementalPayload = {
    itemIndex: itemIndex,
    itemQuantity: itemQuantity,
    lineItems: lineItems,
    sku: SKU,
    callback: setItemQuantity,
    dispatch: dispatch,
  };

  const handleOnChangeQuantity = (value: any) => {
    if (isNaN(Number(value))) {
      setQtyInputValue(qtyInputValue);
      return;
    }
    setQtyInputValue(Number(value));
  };

  const handleUpdateQuantityPayload = {
    itemQuantity: qtyInputValue,
    prevQuantity: itemQuantity,
    availability: availability,
    lineItems: lineItems,
    sku: SKU,
    callback: setQtyInputValue,
    dispatch: dispatch,
  };

  const ButtonCartAddOrUpdateProps = {
    decrementalPayload: decrementalPayload,
    handleOnChangeQuantity: handleOnChangeQuantity,
    handleUpdateQuantityPayload: handleUpdateQuantityPayload,
    incrementalPayload: incrementalPayload,
    isOnStock: isOnStock,
    itemQuantity: itemQuantity,
    qtyInputValue: qtyInputValue,
    currentProduct: currentProduct,
    productPrice: productPrice,
    productDetailPage: true,
  };

  const { favoritesList, handleAddFavorite, handleRemoveFavorite } =
    useFavorites();

  const [isFavorite, setIsFavorite] = useState(false);

  const handleFavorite = () => {
    favIcon === "heart-outlined"
      ? setFavIcon("heart-filled")
      : setFavIcon("heart-outlined");
    if (isFavorite) {
      handleRemoveFavorite(SKU, setIsFavorite);
    } else {
      handleAddFavorite(SKU, setIsFavorite);
    }
    setIsFavorite(!isFavorite);
  };

  const { isLoggedIn } = useCustomer();

  const { getFavoritesList } = useFavorites();

  const getFavoritesListMemo = useCallback(
    (type) => getFavoritesList(type),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  useEffect(() => {
    isLoggedIn && getFavoritesListMemo("default");
  }, [getFavoritesListMemo, isLoggedIn]);

  useEffect(() => {
    setIsFavorite(
      favoritesList?.some(
        (item: any) =>
          item?.ctData?.masterData?.current?.masterVariant?.sku === SKU
      )
    );
  }, [favoritesList, setIsFavorite, SKU]);

  useEffect(() => {
    isFavorite ? setFavIcon("heart-filled") : setFavIcon("heart-outlined");
  }, [isFavorite]);

  useEffect(() => {
    setItemQuantity(getItemQty(lineItems, itemIndex)); // TODO from Jared and Jose: could be made into a custom hook for duplicate logic in productTile.tsx
    setQtyInputValue(getItemQty(lineItems, itemIndex));
  }, [lineItems, itemIndex]);

  useBreadcrumb({ currentSlug: { title: title, link: "" } });
  const galleryWidth = `${((width / 3) * 2).toFixed(0)}px`;

  const ref1 = useRef(null);
  const ref2 = useRef(null);

  const isVisibleTablet = useIsVisible(ref2);

  const isVisibleLaptop = useIsVisible(ref1);

  function useIsVisible(ref: any) {
    const [isIntersecting, setIntersecting] = useState(false);

    useEffect(() => {
      const observer = new IntersectionObserver(([entry]) =>
        setIntersecting(entry.isIntersecting)
      );

      observer.observe(ref.current);
      return () => {
        observer.disconnect();
      };
    }, [ref]);

    return isIntersecting;
  }

  const [isActive, setActive] = useState(true);
  const toggleReadMore = () => setActive(!isActive);

  const scrollToReviews = () => {
    if (reviewsRef.current) {
      const reviews = reviewsRef.current.getBoundingClientRect();
      const offset = reviews.top - 250;
      window.scrollTo({ top: window.scrollY + offset, behavior: "smooth" });
    }
  };

  const scrollToReadMore = () => {
    window.scrollTo(0, 0);
  };

  const handleTitleExpand = () => {
    setIsTitleExpanded((prevState) => !prevState);
  };

  return (
    <section className={classes["product-details"]}>
      <div className={classes["breadcrumb-and-gallery"]}>
        <Breadcrumb />
        <div className={classes["product-gallery"]}>
          {imageSet ? (
            <ProductGallery imageSet={imageSet} />
          ) : (
            <div style={{ display: "block", width: galleryWidth }}></div>
          )}

          {!isMobile && (
            <div className={classes["product-accordion__expandable__desktop"]}>
              <ProductAccordion accordionData={accordionData} />
            </div>
          )}
        </div>
      </div>
      <div className={classes["product-info"]}>
        <div
          style={{
            display: "inline-block",
            width: "auto",
          }}
        >
          <h1
            onClick={() => handleTitleExpand()}
            onKeyDown={() => handleTitleExpand()} //added just to avoid sonarlint issue as this is no interactive element
            className={`${classes["product-title"]} ${
              isMobile ? classes["mobile-product-title"] : ""
            }
            ${isMobile && isTitleExpanded ? classes["expanded"] : ""}`}
          >
            {title}
          </h1>
        </div>
        {(!isOnStock || badges.length > 0) && (
          <div className={classes["badge-container"]}>
            {!isOnStock ? (
              <Badge key="badge-999" type={"out-of-stock"}>
                {productData?.pageContent?.outOfStockBadge}
              </Badge>
            ) : (
              badges.map(
                (badge, i) =>
                  i < 2 && (
                    <Badge key={`badge-${i}`} type={"badge"}>
                      {badge}
                    </Badge>
                  )
              )
            )}
          </div>
        )}

        <div>
          <StarRating
            size="1.25rem"
            color="#FEC600"
            productId={SKU}
            reviewRef={reviewsRef}
          />
        </div>
        {isMobileAndNotTablet && (
          <>
            <span className={classes.weight}>
              {UOM === "KG" || UOM === "LB"
                ? `${qtyPerPackage ? qtyPerPackage : ""} ${netWeight}`
                : ""}
            </span>
            <>
              <div
                className={
                  unitPrice
                    ? classes["price-per-unit"]
                    : classes["price-per-unit-hide"]
                }
              >
                <span>{unitPrice}</span>
                <span>{isCanadianLocale(currentLocale) ? "|" : ""}</span>
                <span>
                  {isCanadianLocale(currentLocale) ? unitPriceCA : ""}
                </span>
              </div>
              <div className={classes["variable-min-max-weight"]}>
                {variableMinAndMaxWeight}
              </div>
            </>
          </>
        )}
        <span ref={ref1}>
          {!isMobileAndNotTablet && countryOfOriginLabel && countryOfOrigin ? (
            <span className={classes.country}>
              {countryOfOriginLabel}: {countryOfOrigin}
            </span>
          ) : (
            <></>
          )}
        </span>

        {!isMobileAndNotTablet && (
          <div>
            <ReadMore
              minLength={width <= 1200 ? 40 : 65}
              readMore={productData?.pageContent?.readMoreLabel}
              readLess={productData?.pageContent?.readLessLabel}
              isActive={isActive}
              toggleReadMore={toggleReadMore}
            >
              {description}
            </ReadMore>
          </div>
        )}

        {productSetItems?.length && (
          <>
            <div className={classes["product-set__title-wrapper"]}>
              <div className={classes["product-set__title-wrapper__icon"]}>
                <Icon iconName={"box-outlined"} />
              </div>
              <h3>{"What's Included?"}</h3>
            </div>
            <div className={classes["product-set__content-list"]}>
              <ul>
                {productSetItems?.map((productItem) => (
                  <li key={productItem.name}>
                    <Link
                      href={`/${currentLocale}/products/${productItem.slug}`}
                    >
                      {productItem.name}
                    </Link>{" "}
                    x {productItem.lineQuantity ? productItem.lineQuantity : ""}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        <div ref={ref2} className={classes["product-info__footer"]}>
          {(isVisibleLaptop && width >= 768) ||
          (isVisibleTablet && width < 768) ? (
            <div className={classes["product-info__footer_dynamic_hide"]}>
              <div
                className={classes["product-info__footer_dynamic_align_left"]}
              >
                <button
                  className={classes["dynamic-redirecting-product-info"]}
                  onClick={() => {
                    scrollToReadMore();
                    setActive(false);
                  }}
                >
                  Product Info
                </button>
                <button
                  className={classes["dynamic-redirecting-reviews"]}
                  onClick={scrollToReviews}
                >
                  Reviews
                </button>
              </div>
              <div
                className={classes["product-info__footer_dynamic_align_right"]}
              >
                <div className={classes["dynamic-price"]}>{totalPrice}</div>
                {isMobileAndNotTablet && (
                  <div
                    className={classes["favorites-btn"]}
                    onClick={handleFavorite}
                  >
                    <Icon iconName={favIcon} />
                  </div>
                )}
                <div className={classes["btn"]}>
                  <ButtonCartAddOrUpdate {...ButtonCartAddOrUpdateProps} />
                </div>
              </div>
            </div>
          ) : (
            <div className={classes["product-info__footer_dynamic_show"]}>
              <div
                className={classes["product-info__footer_dynamic_align_left"]}
              >
                <button
                  className={classes["dynamic-redirecting-product-info"]}
                  onClick={() => {
                    scrollToReadMore();
                    setActive(false);
                  }}
                >
                  Product Info
                </button>
                <button
                  className={classes["dynamic-redirecting-reviews"]}
                  onClick={scrollToReviews}
                >
                  Reviews
                </button>
              </div>
              <div
                className={classes["product-info__footer_dynamic_align_right"]}
              >
                <div className={classes["dynamic-price"]}>{totalPrice}</div>
                {isMobileAndNotTablet && (
                  <div
                    className={classes["favorites-btn"]}
                    onClick={handleFavorite}
                  >
                    <Icon iconName={favIcon} />
                  </div>
                )}
                <div className={classes["btn"]}>
                  <ButtonCartAddOrUpdate {...ButtonCartAddOrUpdateProps} />
                </div>
              </div>
            </div>
          )}
          <div className={classes.price}>{totalPrice}</div>
          {isMobileAndNotTablet && isFavoritesEnableForCA && (
            <div className={classes["favorites-btn"]} onClick={handleFavorite}>
              <Icon iconName={favIcon} />
            </div>
          )}
          <div className={classes["button"]}>
            <ButtonCartAddOrUpdate {...ButtonCartAddOrUpdateProps} />
          </div>
          {!isMobileAndNotTablet && isFavoritesEnableForCA && (
            <div className={classes["favorites-btn"]} onClick={handleFavorite}>
              <Icon iconName={favIcon} />
            </div>
          )}
        </div>
        {!isMobileAndNotTablet && (
          <div className={classes["weight-info"]}>
            <span className={classes.weight}>
              {UOM === "KG" || UOM === "LB"
                ? `${qtyPerPackage ? qtyPerPackage : ""} ${netWeight}`
                : ""}
            </span>
            <>
              <div
                className={
                  unitPrice
                    ? classes["price-per-unit"]
                    : classes["price-per-unit-hide"]
                }
              >
                <span>{unitPrice}</span>
                <span>{isCanadianLocale(currentLocale) ? "|" : ""}</span>
                <span>
                  {isCanadianLocale(currentLocale) ? unitPriceCA : ""}
                </span>
              </div>
              <div className={classes["variable-min-max-weight"]}>
                {variableMinAndMaxWeight}
              </div>
            </>
          </div>
        )}

        {isMobileAndNotTablet && (
          <>
            {countryOfOriginLabel && countryOfOrigin ? (
              <span className={classes.country}>
                {countryOfOriginLabel}: {countryOfOrigin}
              </span>
            ) : (
              <></>
            )}
            <div>
              <ReadMore
                minLength={65}
                readMore={productData?.pageContent?.readMoreLabel}
                readLess={productData?.pageContent?.readLessLabel}
                isActive={isActive}
                toggleReadMore={toggleReadMore}
              >
                {description}
              </ReadMore>
            </div>
          </>
        )}

        {!isMobileAndNotTablet && !isFixedPrice && (
          <div className={classes.pricingMessage}>
            <i>{finalPriceMessage}</i>
          </div>
        )}
        {isMobile && (
          <div>
            <ProductAccordion accordionData={accordionData} />
          </div>
        )}
      </div>
    </section>
  );
};
